import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Paper, Chip } from '@mui/material';
// Only import the icons we're actually using
import SearchIcon from '@mui/icons-material/Search';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';

// Removed local ResizeObserver hack in favor of the global solution in App.jsx

const getStepIcon = (stepId, isActive, isComplete) => {
  const commonProps = { 
    fontSize: 'medium',
    color: isComplete ? 'success' : (isActive ? 'primary' : 'disabled'),
    sx: { mr: 1.5 }
  };
  
  switch(stepId) {
    case 'query':
      return <SearchIcon {...commonProps} />;
    case 'retrieve':
      return <PsychologyIcon {...commonProps} />;
    case 'analyze':
      return <AnalyticsIcon {...commonProps} />;
    case 'synthesize':
      return <AutoAwesomeIcon {...commonProps} />;
    case 'structure':
      return <FormatListBulletedIcon {...commonProps} />;
    default:
      return null;
  }
};

const ProgressStep = ({ id, active, completed, text, isLast, substeps }) => {
  // Add component-level protection against ResizeObserver errors
  React.useLayoutEffect(() => {
    // Empty ResizeObserver just to intercept any potential errors
    const ro = new ResizeObserver(() => {});
    // Cleanup on unmount
    return () => ro.disconnect();
  }, []);
  
  // Using static progress values to completely avoid state changes and rerenders
  // This prevents ResizeObserver issues
  const progress = completed ? 100 : (active ? 70 : 0);

  return (
    <Box sx={{ mb: isLast ? 0 : 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {getStepIcon(id, active, completed)}
        <Typography 
          variant="body1" 
          fontWeight={active || completed ? 600 : 400}
          color={completed ? 'success.main' : (active ? 'primary.main' : 'text.secondary')}
        >
          {text}
          {active && !completed && (
            <Chip 
              label="In Progress" 
              size="small" 
              color="primary" 
              variant="outlined"
              sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
            />
          )}
          {completed && (
            <Chip 
              label="Completed" 
              size="small" 
              color="success" 
              variant="outlined"
              sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
            />
          )}
        </Typography>
      </Box>
      
      {(active || completed) && (
        <Box sx={{ ml: 4 }}>
          <LinearProgress 
            variant={active && !completed ? "determinate" : "determinate"} 
            value={progress} 
            color={completed ? "success" : "primary"}
            sx={{ 
              height: 6, 
              borderRadius: 3,
              mb: 1,
              bgcolor: 'rgba(156, 39, 176, 0.08)'
            }} 
          />
          
          {substeps && substeps.length > 0 && active && (
            <Box sx={{ ml: 2, mt: 1 }}>
              {substeps.map((substep, idx) => (
                <Box
                  key={idx}
                  sx={{
                    opacity: 1
                    // Removed animation to prevent ResizeObserver issues
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      color: 'text.secondary',
                      mb: 0.5,
                      fontStyle: 'italic'
                    }}
                  >
                    {substep}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const ReviewProgress = ({ currentStep }) => {
  const steps = [
    { 
      id: 'query', 
      text: 'Processing user query',
      substeps: ['Analyzing paper abstract', 'Extracting key concepts', 'Formulating search parameters']
    },
    { 
      id: 'retrieve', 
      text: 'Retrieving relevant passages from selected papers',
      substeps: ['Scanning research papers', 'Identifying relevant sections', 'Extracting key passages'] 
    },
    { 
      id: 'analyze', 
      text: 'Analyzing and extracting key information',
      substeps: ['Detecting methodology patterns', 'Identifying research findings', 'Categorizing approaches']
    },
    { 
      id: 'synthesize', 
      text: 'Synthesizing literature review',
      substeps: ['Comparing methodologies', 'Identifying research gaps', 'Establishing relationships between findings']
    },
    { 
      id: 'structure', 
      text: 'Structuring final review',
      substeps: [
        'Introduction and Background',
        'Core Methodologies',
        'Key Findings and Patterns',
        'Research Implications',
        'Future Directions'
      ]
    },
  ];

  return (
    <Box sx={{ opacity: 1 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mt: 2,
          mb: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid rgba(156, 39, 176, 0.2)',
          boxShadow: '0 4px 20px rgba(156, 39, 176, 0.1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: 'linear-gradient(135deg, #9c27b0 20%, #673ab7 80%)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'rgba(156, 39, 176, 0.1)',
              mr: 2
            }}
          >
            <AutoStoriesIcon sx={{ color: '#9c27b0' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#9c27b0' }}>
              Generating Literature Review
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Processing selected papers and generating comprehensive analysis
            </Typography>
          </Box>
        </Box>

        <LinearProgress 
          sx={{ 
            mb: 3, 
            height: 8, 
            borderRadius: 4,
            bgcolor: 'rgba(156, 39, 176, 0.08)'
          }} 
        />

        <Box>
          {steps.map((step, index) => {
            // A step is active if it's the current step
            const isActive = index + 1 === currentStep;
            // A step is completed if we've moved past it
            const isCompleted = currentStep > index + 1;
            
            return (
              <ProgressStep
                key={index}
                id={step.id}
                text={step.text}
                active={isActive}
                completed={isCompleted}
                isLast={index === steps.length - 1}
                substeps={isActive ? step.substeps : undefined}
              />
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default ReviewProgress; 